import { PendingLeaderboardFields } from "components/events/judging/pending/usePendingLeaderboard";
import { getFirstPendingRide } from "components/events/judging/pending/usePendingResultChanges";
import { HeatResult } from "graphql/generated/types";

export type PendingDetail = {
    resultIndex: number;
    ride?: HeatResult["rides"][number];
};

export type PendingScoresAndTies = {
    pendingScorePlaces: Record<string, PendingDetail>;
    pendingTiedPlaces: Record<
        string,
        {
            isPending: boolean;
            results: PendingDetail[];
        }
    >;
    actionItems: {
        index: number;
        isTied: boolean;
        ride?: HeatResult["rides"][number];
    }[];
};

export function usePendingScoresAndTies(
    leaderboard: PendingLeaderboardFields,
): PendingScoresAndTies {
    const seenOnce: PendingScoresAndTies["pendingTiedPlaces"] = {};
    const pendingTiedPlaces: PendingScoresAndTies["pendingTiedPlaces"] = {};
    const pendingScorePlaces: PendingScoresAndTies["pendingScorePlaces"] = {};

    const isPendingStore: boolean[] = [];

    leaderboard.result.forEach((result, resultIndex) => {
        const pendingRide = getFirstPendingRide(result);
        isPendingStore.push(!!pendingRide);

        if (result.place && pendingRide) {
            pendingScorePlaces[result.place] = {
                resultIndex,
                ride: pendingRide,
            };
        }

        if (result.total && result.place) {
            if (
                !(result.place in pendingTiedPlaces) &&
                result.place in seenOnce
            ) {
                pendingTiedPlaces[result.place] = seenOnce[result.place];
            }

            const tie = pendingTiedPlaces[result.place];

            if (tie) {
                tie.results.push({ resultIndex, ride: pendingRide });
                tie.isPending = tie.isPending || !!pendingRide;
            } else {
                seenOnce[result.place] = {
                    results: [{ resultIndex, ride: pendingRide }],
                    isPending: !!pendingRide,
                };
            }
        }
    });

    const actionItemScores: PendingScoresAndTies["actionItems"] = [];
    const actionItemTies: PendingScoresAndTies["actionItems"] = [];

    leaderboard.result.forEach((result, resultIndex) => {
        if (!result.place || !isPendingStore[resultIndex]) return;

        if (result.place in pendingTiedPlaces) {
            actionItemScores.push({
                index: resultIndex,
                isTied: true,
                ride: pendingTiedPlaces[result.place].results.find(
                    (r) => r.resultIndex === resultIndex,
                )?.ride,
            });
        } else if (result.place in pendingScorePlaces) {
            actionItemTies.push({
                index: resultIndex,
                isTied: false,
                ride: pendingScorePlaces[result.place].ride,
            });
        }
    });

    return {
        pendingScorePlaces,
        pendingTiedPlaces,
        actionItems: [...actionItemScores, ...actionItemTies],
    };
}
